import { ClipboardList, Gauge, AlertTriangle, Timer, TrendingUp } from "lucide-react";

function oeeColor(v) {
  if (v >= 75) return "#10B981";
  if (v >= 55) return "#F59E0B";
  return "#EF4444";
}

function Gauge_({ value, color }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(value, 100) / 100) * c;
  return (
    <div className="relative h-16 w-16 shrink-0">
      <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#E2E8F0" strokeWidth="7" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold font-mono-data text-slate-800">
        {value}%
      </span>
    </div>
  );
}

export default function KpiCards({ kpis, onFilterDelayed }) {
  if (!kpis) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
      {/* Active Orders */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="kpi-card-active-orders">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Total Active Orders</p>
            <p className="mt-2 text-4xl font-bold font-mono-data text-slate-900">{kpis.active_orders}</p>
          </div>
          <span className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
            <ClipboardList className="h-5 w-5" />
          </span>
        </div>
        <div className="mt-4 flex gap-1.5 text-[11px] font-medium">
          <span className="text-slate-500">{kpis.pending_orders} pending</span>
          <span className="text-slate-300">·</span>
          <span className="text-amber-600">{kpis.in_progress_orders} running</span>
        </div>
      </div>

      {/* OEE */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="kpi-card-oee">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Equipment Effectiveness</p>
            <p className="mt-2 text-sm text-slate-500">OEE Estimate</p>
          </div>
          <span className="h-10 w-10 rounded-lg bg-sky-50 flex items-center justify-center text-sky-600">
            <Gauge className="h-5 w-5" />
          </span>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <Gauge_ value={kpis.oee_estimate} color={oeeColor(kpis.oee_estimate)} />
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Avail {kpis.availability}%<br />
            {kpis.active_machines}/{kpis.total_machines} lines active
          </div>
        </div>
      </div>

      {/* Delayed */}
      <button
        onClick={onFilterDelayed}
        className="text-left bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-red-300 transition-all"
        data-testid="kpi-card-delayed-orders"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Delayed Orders</p>
            <p className={`mt-2 text-4xl font-bold font-mono-data ${kpis.delayed_orders > 0 ? "text-red-600" : "text-slate-900"}`}>
              {kpis.delayed_orders}
            </p>
          </div>
          <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${kpis.delayed_orders > 0 ? "bg-red-50 text-red-600" : "bg-slate-50 text-slate-400"}`}>
            <AlertTriangle className="h-5 w-5" />
          </span>
        </div>
        <div className="mt-4 flex items-center gap-2 text-[11px] font-medium">
          <span className="text-slate-400">{kpis.delayed_orders > 0 ? "Tap to focus delayed →" : "All on schedule"}</span>
          {kpis.urgent_orders > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold" data-testid="kpi-urgent-count" title="High-priority jobs not yet overdue">
              <AlertTriangle className="h-3 w-3" /> {kpis.urgent_orders} urgent
            </span>
          )}
        </div>
      </button>

      {/* OTD */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid="kpi-card-otd-rate">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">On-Time Delivery</p>
            <p className="mt-2 text-4xl font-bold font-mono-data text-emerald-600">{kpis.otd_rate}%</p>
          </div>
          <span className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
            <Timer className="h-5 w-5" />
          </span>
        </div>
        <div className="mt-4 flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
          <TrendingUp className="h-3.5 w-3.5" />
          Target 95% · {kpis.completed_orders} completed
        </div>
      </div>
    </div>
  );
}
